import React, { useState } from 'react';
import { supabase } from '../services/supabase';
import { Lock, Check } from 'lucide-react';
import { useI18n } from '../i18n';

export default function UpdatePassword({ onSuccess }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setMessage(t('updatePassword.passwordsDoNotMatch'));
      return;
    }

    if (password.length < 6) {
      setMessage(t('updatePassword.passwordMinLength'));
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      setSuccess(true);
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 2000);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md w-full">
          <div className="bg-white p-6 sm:p-8 rounded-xl border border-gray-200 text-center">
            <div className="bg-verde-100 text-verde-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5">
              <Check size={28} />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('updatePassword.successTitle')}
            </h2>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              {t('updatePassword.successBody')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="max-w-md w-full">
        <div className="bg-white p-6 sm:p-8 rounded-xl border border-gray-200">
          <div className="text-center mb-8">
            <div className="inline-flex p-3 rounded-xl bg-lavender-100 text-lavender-600 mb-4">
              <Lock size={28} />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">
              {t('updatePassword.title')}
            </h1>
            <p className="text-sm text-gray-500">
              {t('updatePassword.subtitle')}
            </p>
          </div>

          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t('updatePassword.newPassword')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3.5 text-gray-400" size={20} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-colors"
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t('updatePassword.confirmPassword')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3.5 text-gray-400" size={20} />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-colors"
                />
              </div>
            </div>

            {message && (
              <div className="p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-700">
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !password.trim() || !confirmPassword.trim()}
              className="w-full bg-gray-900 text-white h-10 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm transition-all"
            >
              {loading ? t('common.updating') : t('updatePassword.submit')}
            </button>
          </form>
        </div>

        <div className="mt-6 bg-gray-50 border border-gray-200 p-4 rounded-lg text-sm">
          <p className="text-sm text-gray-600">
            {t('updatePassword.tip')}
          </p>
        </div>
      </div>
    </div>
  );
}
